/**
 * Detect missing commas in SELECT lists (same-line and across lines).
 * High-confidence patterns only — especially table.col table.col adjacency,
 * which Oracle/BIP often misreport as unrelated faults.
 */

function offsetToLineCol(str, offset) {
  const before = str.substring(0, Math.max(0, offset));
  const lines = before.split(/\r?\n/);
  return { line: lines.length, column: (lines[lines.length - 1] || '').length + 1 };
}

/**
 * @param {string} sql
 * @returns {null|{ error: string, suggestion: string, line: number, column: number, index: number, left: string, right: string }}
 */
export function findMissingSelectComma(sql) {
  if (!sql || typeof sql !== 'string') return null;
  const trimmed = sql.trim();
  const selectMatch = trimmed.match(/\bselect\b\s+(?:distinct\s+|all\s+|unique\s+)?([\s\S]+?)\s+\bfrom\b/i);
  if (!selectMatch) return null;

  const selectList = selectMatch[1];
  const selectListStart = trimmed.indexOf(selectMatch[1], trimmed.search(/\bselect\b/i));

  // 1) Same line: alias.col alias.col  (missing comma) — very high confidence
  {
    const re =
      /\b([A-Za-z][A-Za-z0-9_$#]*\.[A-Za-z][A-Za-z0-9_$#]*)\s+([A-Za-z][A-Za-z0-9_$#]*\.[A-Za-z][A-Za-z0-9_$#]*)\b/g;
    const m = re.exec(selectList);
    if (m) {
      const index = selectListStart + m.index + m[1].length;
      const { line, column } = offsetToLineCol(trimmed, index);
      return {
        error: `Missing comma between SELECT columns "${m[1]}" and "${m[2]}".`,
        suggestion: `Add a comma: ${m[1]}, ${m[2]}`,
        line,
        column,
        index,
        left: m[1],
        right: m[2],
      };
    }
  }

  // 2) Same line: bare_col alias.col  (second token looks like table.column, not a simple alias)
  {
    const re =
      /\b([A-Za-z][A-Za-z0-9_$#]*)\s+([A-Za-z][A-Za-z0-9_$#]*\.[A-Za-z][A-Za-z0-9_$#]*)\b/g;
    let m;
    while ((m = re.exec(selectList)) !== null) {
      const left = m[1].toUpperCase();
      if (left === 'AS' || left === 'DISTINCT' || left === 'UNIQUE' || left === 'ALL') continue;
      // Skip SQL keywords / clause words that can sit before qual.col
      if (
        [
          'WHEN', 'THEN', 'ELSE', 'END', 'CASE', 'AND', 'OR', 'NOT', 'IN', 'IS', 'LIKE',
          'BETWEEN', 'EXISTS', 'ON', 'USING', 'JOIN', 'BY', 'PARTITION', 'ORDER', 'FROM',
          'WHERE', 'SET', 'INTO', 'VALUES', 'SELECT', 'HAVING', 'UNION', 'MINUS',
        ].includes(left)
      ) {
        continue;
      }
      const before = selectList.slice(Math.max(0, m.index - 4), m.index);
      if (/\bAS\s*$/i.test(before + ' ')) continue;
      const index = selectListStart + m.index + m[1].length;
      const { line, column } = offsetToLineCol(trimmed, index);
      return {
        error: `Missing comma between SELECT columns "${m[1]}" and "${m[2]}".`,
        suggestion: `Add a comma: ${m[1]}, ${m[2]} (a SELECT alias cannot be a table.column reference)`,
        line,
        column,
        index,
        left: m[1],
        right: m[2],
      };
    }
  }

  return null;
}

